<template>
  <div class="weekly-view-weekly-quantities-template w-full max-w-full min-w-0">
    <weekly-quantities-template-tab
      :project-id="projectId"
      :roots="roots"
      :nodes-by-id="nodesById"
      :loading="loading"
      :mes-inicio="mesInicio"
      :month-labels="monthLabelsArr"
      :hh-semana="hhSemana"
      :semana-actual="semanaActual"
      :previous-weeks="previousWeeks"
      :week-start-date="weekStartDate"
      :week-end-date="weekEndDate"
      :project-weeks="projectWeeks"
      :weekly-api-params="weeklyApiParams"
      :expanded-ids="expandedIds"
      @update:previous-weeks="onPreviousWeeks"
      @toggle="toggleExpand"
      @children-loaded="onChildrenLoaded"
    />
  </div>
</template>

<script>
/**
 * Plantilla de cantidades semanales.
 * Semana actual y fechas: weekly-planning-header / project weeks.
 * Ventana API: current_week + previous_weeks + max_week (+2 posteriores si hay).
 * FTD/HH por semana: mapas del API (with_takeoff_weekly_stats); sin recálculo en Vue.
 */
import axios from 'axios'
import { mapGetters } from 'vuex'
import WeeklyQuantitiesTemplateTab from './hh-semanal/weekly-quantities-template-tab.vue'
import { mapBoqRootToWeeklyNode } from './hh-semanal/takeoffWeeklyNodes.js'
import {
  monthLabels,
  qntHorasDisponivel,
} from './hh-semanal/gestionHHCalc.js'

function premisesFromProject(project) {
  const raw = (project && project.configs && project.configs.takeoff_premises) || {}
  return {
    horasSemana: Number(raw.weekly_hours) || 0,
    perdaFolga: Number(raw.leave_loss_percent) || 0,
    horasExtras: Number(raw.overtime_hours_percent) || 0,
    perdaFeriado: Number(raw.holiday_loss_percent) || 0,
  }
}

export default {
  name: 'weekly-view-weekly-quantities-template',

  components: {
    WeeklyQuantitiesTemplateTab,
  },

  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    projectWeeks: {
      type: Array,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      loading: true,
      roots: [],
      expandedIds: [],
      nodesById: {},
      previousWeeks: 3,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),
    semanaActual() {
      return this.weekNumber > 0 ? Number(this.weekNumber) : 1
    },
    maxWeek() {
      const weeks = this.projectWeeks || []
      if (!weeks.length) {
        return this.semanaActual
      }
      return weeks.reduce((max, week) => {
        const n = Number(week && week.number) || 0
        return n > max ? n : max
      }, this.semanaActual)
    },
    weeklyApiParams() {
      return {
        with_takeoff_weekly_stats: 1,
        // Params planos: axios GET serializa objetos como JSON string.
        current_week_number: this.semanaActual,
        current_week_start: this.weekStartDate || null,
        current_week_end: this.weekEndDate || null,
        // Compat: también objeto (backend acepta JSON string o array).
        current_week: {
          number: this.semanaActual,
          start: this.weekStartDate || null,
          end: this.weekEndDate || null,
        },
        previous_weeks: this.previousWeeks,
        max_week: this.maxWeek,
      }
    },
    mesInicio() {
      const start = this.project && this.project.current_start_date
      if (start) {
        return String(start).slice(0, 7)
      }
      return currentYearMonth()
    },
    numMeses() {
      const start = this.project && this.project.current_start_date
      const end = this.project && this.project.current_end_date
      if (!start || !end) {
        return 12
      }
      const [y0, m0] = String(start).split('-').map(Number)
      const [y1, m1] = String(end).split('-').map(Number)
      if (!y0 || !m0 || !y1 || !m1) {
        return 12
      }
      return Math.max(1, (y1 - y0) * 12 + (m1 - m0) + 1)
    },
    monthLabelsArr() {
      return monthLabels(this.mesInicio, this.numMeses)
    },
    hhSemana() {
      return qntHorasDisponivel(premisesFromProject(this.project))
    },
  },

  watch: {
    projectId(value) {
      if (!value) {
        this.roots = []
        this.nodesById = {}
        this.expandedIds = []
      }
    },
    weeklyApiParams: {
      deep: true,
      immediate: true,
      handler() {
        if (this.projectId) {
          this.loadRoots()
        }
      },
    },
  },

  methods: {
    registerNodes(nodes) {
      const next = { ...this.nodesById }
      ;(nodes || []).forEach((node) => {
        next[node.id] = node
      })
      this.nodesById = next
    },
    async loadRoots() {
      if (!this.projectId) {
        this.roots = []
        return
      }
      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`,
          {
            params: {
              only_takeoff: 1,
              ...this.weeklyApiParams,
            },
          },
        )
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []
        this.roots = list.map(mapBoqRootToWeeklyNode)
        this.nodesById = {}
        this.registerNodes(this.roots)
        this.expandedIds = []
      } catch (error) {
        this.roots = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loading = false
      }
    },
    toggleExpand(nodeId) {
      if (this.expandedIds.includes(nodeId)) {
        this.expandedIds = this.expandedIds.filter((id) => id !== nodeId)
        return
      }
      this.expandedIds = [...this.expandedIds, nodeId]
    },
    onChildrenLoaded({ nodes }) {
      this.registerNodes(nodes)
    },
    onPreviousWeeks(value) {
      this.previousWeeks = value
    },
  },
}

function currentYearMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}
</script>
